import { GameState, Player, Army, Team, SoldierType, UnitStats } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Token provider function - will be set by the app
let getAccessToken: (() => Promise<string>) | null = null;

// Set the token provider
export function setTokenProvider(provider: () => Promise<string>) {
  getAccessToken = provider;
}

// Helper function to get auth headers
async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (err) {
      console.error('Failed to get access token:', err);
    }
  }

  return headers;
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

// API Service
export const api = {
  // Health check
  async health(): Promise<{ status: string; time: string }> {
    const response = await fetch(`${API_URL}/health`);
    return handleResponse(response);
  },

  // Game State
  async getGameState(): Promise<GameState> {
    const response = await fetch(`${API_URL}/game-state`);
    return handleResponse(response);
  },

  async resetGame(): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/reset`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Players
  async getAllPlayers(): Promise<Player[]> {
    const response = await fetch(`${API_URL}/players`);
    return handleResponse(response);
  },

  async getPlayer(id: string): Promise<Player> {
    const response = await fetch(`${API_URL}/players/${id}`);
    return handleResponse(response);
  },

  async getPlayerByUserId(userId: string): Promise<Player | null> {
    try {
      const response = await fetch(`${API_URL}/players/by-user/${userId}`, {
        headers: await getAuthHeaders(),
      });
      if (response.status === 404) {
        return null;
      }
      return handleResponse(response);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  },

  async createPlayer(name: string, team: Team, userId?: string): Promise<Player> {
    const response = await fetch(`${API_URL}/players`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ name, team, userId }),
    });
    return handleResponse(response);
  },

  async updatePlayer(
    id: string,
    updates: {
      name?: string;
      team?: Team;
      raidPoints?: number;
      army?: Army;
      userId?: string;
    }
  ): Promise<Player> {
    const response = await fetch(`${API_URL}/players/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  async deletePlayer(id: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/players/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async resetPlayerArmy(id: string): Promise<Player> {
    const response = await fetch(`${API_URL}/players/${id}/reset`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Unit Stats
  async getAllUnitStats(): Promise<Record<SoldierType, UnitStats>> {
    const response = await fetch(`${API_URL}/unit-stats`);
    return handleResponse(response);
  },

  async updateUnitStats(
    type: SoldierType,
    updates: {
      hp?: number;
      damage?: number;
      cost?: number;
      imageUrl?: string;
    }
  ): Promise<UnitStats> {
    const response = await fetch(`${API_URL}/unit-stats/${type}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  // Battlefield Settings
  async updateBattlefieldBackground(backgroundUrl: string): Promise<{ message: string; backgroundUrl: string }> {
    const response = await fetch(`${API_URL}/battlefield/background`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ backgroundUrl }),
    });
    return handleResponse(response);
  },

  async updateKingImages(raidersImageUrl: string, defendersImageUrl: string): Promise<{ message: string; raidersImageUrl: string; defendersImageUrl: string }> {
    const response = await fetch(`${API_URL}/king-images`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ raidersImageUrl, defendersImageUrl }),
    });
    return handleResponse(response);
  },
};

export default api;
